import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _kebabCase from 'lodash/kebabCase';
import { DataLayerTransformer, inventoryTransformer as PixallInventoryTransformer } from '../transformers/pixall/index.js';
import { getCDLVehicleTransformer } from '../transformers/pixall/inventoryTransformer';
import { addListingTriggerData, isPremiumSpotlight, getBrandUnit, getInventoryOwnerById, getListingCategory, isVDP, isHybridVDPPage, transformTileType, transformCoxAutoDataLayer } from '../utilities/index.js';
import { productTileClickHelper } from './helpers/index.js';
import { checkShouldIncludeClickType } from '../analytics/google/helpers/index.js';
import { Pixall, Google } from '../analytics/index.js';
import { valueEventCode } from '../constants/index.js';
import { inventoryTransformer as googleInventoryTransformer } from '../transformers/google/index.js';
const evSuffixMap = {
  'my-at': '_myat',
  'my-kbb': '_mykbb'
};

/**
 * @summary sends Google impressions for NCCO listings
 * @data inventoryId, ownerId, inheritPageData
 */
export async function preorderInventoryImpressions(metadata, option) {
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    inventoryId,
    ownerId
  } = data;
  const pageName = _get(dataLayer, 'page.pageName', '');
  const inheritPageData = !['vdp'].includes(pageName);
  const inventory = getInventoryOwnerById(state, inventoryId);
  const vehicleData = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  Google.sendImpressions(metadata, {
    event: 'analytics_event',
    eventElement: 'dealer',
    eventAction: 'impression',
    eventResult: 'cta::request-a-quote::new-car-custom-order',
    eventNonInteractive: true,
    eventData: {
      vehicle: {
        dealershipId: ownerId,
        ...(!inheritPageData && vehicleData)
      }
    }
  }, option);
  if (pageName === 'srp') {
    Pixall.sendImpression({
      dataLayer,
      history
    }, {
      eventSource: 'dealership',
      label: 'search-results-page',
      listingCode: ownerId,
      listingType: 'newcarorder',
      feature: 'New Car Order'
    }, option);
  }
  return {};
}

/**
 * @summary Sends Pixall/Google impressions for listings
 * @data cmp, pixallData, resultsDuck/selectedItemIds
 */
export function inventoryImpressions(metadata, option) {
  const {
    dataLayer = {},
    data = {},
    state,
    resultsDuck,
    history
  } = metadata;
  const {
    cmp,
    pixallData = {},
    selectedItemIds = [],
    startingIndex,
    paginatedListingIndex,
    dealDetails,
    isSingle = true
  } = data;
  let resultsWithOwner = [];
  if (resultsDuck || selectedItemIds?.length > 0 || !_isEmpty(dealDetails)) {
    let results;
    let eventResult = '[list_name]';
    let evSuffix = '';
    const siteSection = dataLayer?.page?.siteSection;

    // use of results duck should be deprecated
    if (resultsDuck) {
      results = resultsDuck.selectors.getActiveInventory(state);
    } else if (!_isEmpty(dealDetails)) {
      results = Object.keys(dealDetails).map(key => dealDetails[key]);
    } else {
      results = selectedItemIds.map(id => ({
        id
      }));
    }
    const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
    const isDealer = cmp === 'dealer' || cmp === 'dealer_alp';
    const ecommerceCategory = isDealer ? 'ecom_dealer' : 'ecom_vehicle-inventory';
    resultsWithOwner = results.map(function (inventory) {
      if (inventory === void 0) {
        inventory = {};
      }
      return getInventoryOwnerById(state, inventory.id, isDealer);
    });
    const isRegularOrSuppListing = cmp === 'lstg' || cmp === 'sup';
    switch (cmp) {
      case 'alp':
      case 'dealer_alp':
        eventResult = 'alpha';
        break;
      case 'spv_lstg_':
      case 'spv_lstg_4':
        eventResult = 'spotlight';
        break;
      case 'sim_v_lstg_': // Similar Vehicles carousel
      case 'similar-listing':
        eventResult = 'similar';
        break;
      case 'pvt_v_lstg_':
      case 'pvt_v_lstg':
        eventResult = 'private-seller';
        break;
      case 'dl_v_lstg_':
        // eLot carousels
        eventResult = 'elot';
        evSuffix = evSuffixMap[siteSection] || '';
        break;
      case 'rv_lstg_':
        // recommended inventory carousels
        eventResult = `recommendations${evSuffixMap[siteSection] || ''}`;
        break;
      case 'dealer':
        eventResult = 'dealer_srp';
        break;
      case 'dealerdetails':
        eventResult = 'ddp';
        break;
      case 'dhs_v_lstg_':
        // home services carousel
        eventResult = 'dhs';
        break;
      case 'dealdetails':
        eventResult = 'my-deals';
        break;
      case 'ncb_v_lstg':
        eventResult = 'ncb';
        break;
      default:
        eventResult = cmp;
    }

    /* Send Pixall Impression 25 listings at a time
     * Pixall needs a listing type for each vehicle and one for the overall impression
     * impressionType takes precedence over listingType for the overall impression listing type
     */
    if (!dealDetails) {
      PixallInventoryTransformer.buildListingChunks(resultsWithOwner, {
        rank: pixallData.rank || 1,
        listingType: pixallData.listingType || 'lstg'
      }).forEach(listingChunk => {
        const eventSource = pixallData.isCarousel ? 'vehicle-carousel' : 'vehicle';
        const pixallPayload = {
          eventSource: pixallData.eventSource || eventSource,
          label: pixallData.label ? pixallData.label : pageId,
          listingType: pixallData.impressionType || pixallData.listingType || 'lstg',
          filterValues: pixallData.filterValues,
          hasListingType: pixallData.hasListingType
        };
        if (isDealer || cmp === 'dealerdetails' && !isSingle) {
          pixallPayload.listingCode = listingChunk[0].listingCode;
        }
        if (!isDealer) {
          pixallPayload.vehicles = listingChunk;
        }
        const cdl = {};
        addListingTriggerData(cmp, cdl);
        if (pixallData.impressionType === 'srpi') {
          transformCoxAutoDataLayer({
            listingType: pixallData.listingType,
            cdl,
            dataLayer,
            eventType: 'impression'
          });
        }
        if (Object.keys(cdl).length) {
          pixallPayload.cdl = cdl;
        }
        Pixall.sendImpression({
          dataLayer,
          history
        }, pixallPayload, option);
      });
    }
    if (!isRegularOrSuppListing) {
      Google.sendImpressions(metadata, {
        event: 'ecom_event',
        eventElement: isDealer ? 'dealer' : 'listings',
        eventAction: 'ecom-impression',
        eventResult: eventResult + evSuffix,
        eventNonInteractive: true,
        ecommerce: {
          impressions: results.map((_ref, index) => {
            let {
              id
            } = _ref;
            const vehicleListingPosition = startingIndex >= 0 ? `${startingIndex + index + 1}` : paginatedListingIndex;
            return {
              ...googleInventoryTransformer(dealDetails ? results[index]?.vehicle : getInventoryOwnerById(state, id, isDealer), {
                target: 'ecommerce',
                vehicleListingPosition,
                dataLayer,
                eventResult,
                onlyEcomDims: cmp === 'dealerdetails' ? ['dimension31', 'dimension33'] : undefined
              }),
              id: eventResult,
              name: eventResult,
              category: ecommerceCategory,
              list: ecommerceCategory
            };
          })
        }
      }, option);
    }
  } else {
    throw new Error('resultsDuck or selectedItems is missing');
  }
  return {
    cmp,
    results: resultsWithOwner
  };
}

/**
 * @summary Sends Pixall/Google click event
 * @data filterDuck, inventoryId, pixallData
 * @events event1
 */
export function inventoryClick(_ref2, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref2;
  const {
    inventoryId,
    par,
    indexClicked,
    paginatedListingIndex,
    vehicle,
    context = ''
  } = data;
  let {
    pixallData = {}
  } = data;
  let cmp = 'v_lstg_im';
  if (par) {
    const parentId = par.replace(/_\d+$/, '');
    switch (parentId) {
      case 'alp':
        cmp = 'alp';
        break;
      case 'alp-dlr':
        cmp = 'alp-dlr';
        break;
      case 'alpm':
        cmp = 'alpm';
        break;
      case 'alpm-dlr':
        cmp = 'alpm-dlr';
        break;
      case 'v_lstg':
        cmp = 'v_lstg_im';
        break;
      case 'spv_lstg':
        cmp = 'spv_im';
        break;
      case 'ncb_v_lstg':
      case 'sim_v_lstg':
        cmp = 'sim_v_lstg_im';
        break;
      case 'sim':
        cmp = 'sim';
        break;
      case 'pvt_v_lstg_':
        cmp = 'pvt_v_lstg_im';
        break;
      case 'dl_v_lstg':
      case 'dlr_c_lstg':
      case 'elot':
      case 'compare_elot':
        cmp = 'dl_v_lstg_im';
        break;
      case 'comp_lstg':
        cmp = 'comp_lstg_im';
        break;
      case 'comp_v_lstg':
        cmp = 'comp_v_lstg_im';
        break;
      case 'ddv_lstg':
        cmp = 'ddv_lstg_im';
        break;
      case 'mya_v_lstg':
        cmp = 'myz_v_lstg_im';
        break;
      case 'rv_lstg':
        cmp = 'rv_lstg_im';
        break;
      case 'dhs_v_lstg':
        cmp = 'dhs_v_lstg_im';
        break;
      case 'hot_car':
        cmp = 'hot_car_im';
        break;
      case 'hybrid_v_lstg':
        cmp = 'hybrid_v_lstg_im';
        break;
      case 'sup_v_lstg':
        cmp = 'sup_v_lstg_im';
        break;
      case 'dealer':
        cmp = 'dealer_im';
        break;
      case 'dealerdetails':
        cmp = 'dealerdetails_im';
        break;
      case 'dealdetails':
        cmp = 'dealdetails';
        break;
      case 'ncb':
        cmp = 'ncb_v_lstg_im';
        break;
      case 'goodgreatprice':
        cmp = 'cta::good-great-price::alert';
        break;
      case 'interested':
        cmp = 'cta::still-interested::alert';
        break;
      case 'numviewed':
        cmp = 'cta::number-viewed::alert';
        break;
      case 'reduced':
        cmp = 'cta::reduce-price::alert';
        break;
      case 'ime':
        cmp = 'ime_v_lstg_im';
        break;
      case 'interested_spotlight':
        cmp = 'still-interested::spotlight';
        break;
      default:
        break;
    }
  }
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const detailPageName = dataLayer.page?.detailPageName; // GA detailPageName
  const pageName = _get(dataLayer, 'page.pageName', '');
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isCompare = cmp === 'comp_lstg_im' || cmp === 'comp_v_lstg_im' || par === 'compare_elot';
  const inheritPageData = !['vdp'].includes(detailPageName);
  const vehicleData = googleInventoryTransformer(vehicle ?? inventory, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  const isListingPage = detailPageName === 'build-and-price' || detailPageName === 'srp' || detailPageName === 'srp_no_results';
  const exteriorColor = vehicleData?.exteriorColor?.toUpperCase();
  let label;
  if (isCompare) {
    const compareLabels = {
      comp_lstg: `comp_lstg_im - ${pageId} - compare`,
      comp_v_lstg: 'comp_v_lstg_im - modal',
      compare_elot: 'dl_v_lstg_im - compare modal'
    };
    label = compareLabels[par];
    pixallData = {
      ...pixallData,
      eventSource: 'vehicle'
    };
  } else if (pageName === 'inv') {
    label = cmp === 'v_lstg_im' ? 'cta::view-full-listing-details' : cmp;
  } else if (cmp === 'sim_v_lstg_im') {
    label = cmp;
  } else if (isPremiumSpotlight(par)) {
    label = 'cta::highlight::alert';
    pixallData = {
      ...pixallData,
      eventSource: 'vehicle',
      listingType: 'highlight'
    };
  } else if (cmp === 'alp') {
    label = 'alpha';
  } else if (cmp === 'alp-dlr') {
    label = 'alpha-dealer';
  } else if (cmp === 'alpm') {
    label = 'alpham';
  } else if (cmp === 'alpm-dlr') {
    label = 'alpham-dealer';
  } else if (cmp === 'spv_im') {
    label = 'spotlight';
  } else if (cmp === 'v_lstg_im') {
    label = 'listing';
  } else if (cmp === 'rv_lstg_im') {
    label = cmp;
    pixallData = {
      ...pixallData,
      listingType: 'recommendations'
    };
  } else if (cmp === 'sup_v_lstg_im') {
    label = 'supplemental';
  } else if (context === 'privateSellerListing') {
    label = 'pvt_v_lstg_im';
  } else if (isListingPage) {
    if (['ncb_v_lstg_im', 'ime_v_lstg_im'].includes(cmp) || ['goodgreatprice', 'interested', 'numviewed', 'reduced', 'interested_spotlight'].includes(par)) {
      label = cmp;
      pixallData = {
        ...pixallData,
        eventSource: 'vehicle'
      };
      option = {
        ...option,
        skipPageEvent: true
      };
    } else {
      label = pageName === 'bap' ? `${cmp}` : `${cmp} - ${pageId}`;
    }
  } else if (pageName === 'fyd_srl') {
    label = `${cmp} - ${pageId}`;
  } else {
    label = `VDP - ${pixallData?.eventSource} - ${cmp} - ${pageId}`;
  }
  const {
    eventSource,
    listingType
  } = pixallData;
  const cdl = {
    eventProperties: {
      vehicle: PixallInventoryTransformer.getVehicleCommonDataLayer(inventory)
    }
  };
  if (['alp', 'alp-dlr', 'alpm', 'alpm-dlr', 'spv_im', 'v_lstg_im', 'sup_v_lstg_im'].includes(cmp)) {
    cdl.eventProperties.vehicle = getCDLVehicleTransformer(vehicleData);
    option = {
      ...option,
      skipPageEvent: true
    };
  }
  addListingTriggerData(par, cdl);
  if (eventSource === 'vehicle' || eventSource === 'vehicle-carousel') {
    Pixall.sendClickEvent({
      label,
      eventSource,
      par,
      eventType: 'click',
      inventory,
      exteriorColor,
      listingType,
      ...(par !== 'compare_elot' && isCompare && pageName === 'srp' && {
        listingType: 'compare'
      }),
      cdl
    }, dataLayer, option);
  }
  let eventResult = '[list_name]'; // from POC
  let evSuffix = '';
  const siteSection = dataLayer?.page?.siteSection;

  // note: there are more carousels not yet covered here but can be added
  // to the evSuffixMap if they share cmp values across site sections
  switch (cmp) {
    case 'alp':
      eventResult = 'alpha';
      break;
    case 'spv_lstg_':
    case 'spv_lstg_4':
    case 'spv_im':
      eventResult = 'spotlight';
      break;
    case 'lstg':
    case 'v_lstg_im':
    case 'v_lstg_':
      eventResult = 'listing';
      break;
    case 'sim_v_lstg_im':
      // Similar Vehicles carousel
      eventResult = par === 'ncb_v_lstg' ? 'ncb' : 'similar';
      break;
    case 'pvt_v_lstg_im':
      eventResult = 'private-seller';
      break;
    case 'dl_v_lstg_im':
      // eLot carousels
      eventResult = 'elot';
      evSuffix = evSuffixMap[siteSection] || '';
      break;
    case 'rv_lstg_im':
      // recommended inventory
      eventResult = `recommendations${evSuffixMap[siteSection] || ''}`;
      break;
    case 'sup_v_lstg_im':
      eventResult = 'supplemental';
      break;
    case 'dealer_im':
      eventResult = 'dealer_srp';
      break;
    case 'dealerdetails_im':
      eventResult = 'ddp';
      break;
    case 'comp_lstg_im':
      eventResult = 'compare';
      break;
    case 'comp_v_lstg_im':
      eventResult = 'compare';
      break;
    case 'dhs_v_lstg_im':
      // home services carousel
      eventResult = 'dhs';
      break;
    case 'dealdetails':
      eventResult = 'my-deals';
      break;
    case 'ncb_v_lstg_im':
      eventResult = 'ncb';
      break;
    case 'cta::still-interested::alert':
    case 'cta::number-viewed::alert':
    case 'cta::reduce-price::alert':
    case 'cta::good-great-price::alert':
    case 'cta::highlight:alert':
      eventResult = par;
      break;
    case 'ime_v_lstg_im':
      eventResult = 'ime';
      break;
    default:
  }
  const eventElement = cmp === 'dealer_im' ? 'dealer' : 'listings';
  const ecommerceCategory = cmp === 'dealer_im' ? 'ecom_dealer' : 'ecom_vehicle-inventory';
  Google.sendClickEvent({
    event: 'ecom_event',
    eventElement,
    eventAction: 'ecom-click',
    eventResult: eventResult + evSuffix,
    eventData: cmp === 'dealerdetails_im' ? {} : {
      vehicle: {
        ...vehicleData,
        vehicleListingPosition: indexClicked >= 0 ? `${indexClicked + 1}` : paginatedListingIndex
      }
    },
    eventNonInteractive: cmp === 'dealerdetails_im',
    ecommerce: {
      click: {
        actionField: {
          list: 'ecom_vehicle-inventory'
        },
        products: [{
          ...googleInventoryTransformer(vehicle ?? inventory, {
            target: 'ecommerce',
            vehicleListingPosition: indexClicked >= 0 ? `${indexClicked + 1}` : paginatedListingIndex,
            dataLayer,
            eventResult,
            onlyEcomDims: cmp === 'dealerdetails_im' ? ['dimension31', 'dimension33'] : undefined
          }),
          id: eventResult,
          name: eventResult,
          category: ecommerceCategory
        }]
      }
    }
  }, option);

  // return data for custom event
  return {
    cmp,
    par,
    eventElement,
    eventResult,
    ecommerceCategory
  };
}

/**
 * @summary Sends Pixall/Google Click event on listing save
 * @cmp my_dtc, my_stc, qs_delt_v, qs_v
 * @data inventoryId, suffix, wasSaved, pixallData
 * @events event1, event18, event27
 *
 */
export function inventorySave(_ref3, option) {
  let {
    data,
    dataLayer = {},
    state
  } = _ref3;
  let cmp;
  const {
    inventoryId,
    par = 'v_lstg',
    wasSaved,
    suffix = '',
    pixallData = {},
    inheritPageData,
    wasSticky,
    context = '',
    eventName
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const isLoggedIn = dataLayer?.user?.isLoggedIn && dataLayer.user.isLoggedIn !== '0';
  const detailPageName = dataLayer?.page?.detailPageName; // GA detailPageName
  const pageNameList = ['ddp', 'srp_dealer'];
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isNba = context === 'nba';
  const isBuildAndPricePage = pageId === 'bld_and_pr';
  const chromeStyleId = inventory?.chromeStyleId || inventory?.styleId;
  const isSimilarListingInVDPPage = (pageId === 'fyc_vdp' || pageId === 'fyc_hybrid_vdp') && par === 'sim_v_lstg';
  const isSimilarListingInSRPPage = detailPageName === 'srp' && par === 'sim';
  const isHybridVDP = isHybridVDPPage(dataLayer);

  // user logged in OR not on similar carousel pages (VDP or SRP)
  // not sending pixall when guest user save on similar carousel in VDP and SRP
  const loggedInVdpAndSrp = isLoggedIn || (isHybridVDP || !isSimilarListingInVDPPage) && !isSimilarListingInSRPPage;

  // Successful save/unsave
  if (isLoggedIn) {
    // myatc save
    cmp = wasSaved ? 'my_dtc' : 'my_stc';
  } else {
    // quick save
    cmp = wasSaved ? 'qs_delt_v' : 'qs_v';
  }
  cmp += suffix;
  cmp += wasSticky ? '_stky' : '';
  const listingCategory = getListingCategory(par);
  const shouldIncludeClickType = checkShouldIncludeClickType(detailPageName);
  const recommendationsCategory = listingCategory === 'recommendations';
  let categoryString = ''; // DDP, DSRP, etc.
  // VDP is not a candidate for shouldIncludeClickType due to most saves not requiring it
  if (shouldIncludeClickType || !wasSaved && ['similar', 'recommendations'].includes(listingCategory) && !isHybridVDP) {
    categoryString = `::${listingCategory}`;
  }
  const stickyString = wasSticky ? '::sticky-header' : '';

  // Next Best Action - tells that the CTA is from the Email Confirmation Next Steps modal
  const nbaString = isNba ? '::nba' : '';
  const invString = isHybridVDP ? '::ime' : '';
  const vehicleData = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  }, inheritPageData);
  const exteriorColor = vehicleData?.exteriorColor?.toUpperCase();
  const {
    brand = ''
  } = state;
  const clickType = _get(state, 'query.clickType', '');

  // skip pageId in BAP
  let pageIdLabel = isBuildAndPricePage ? '' : ` - ${pageId}`;
  if (isHybridVDP) {
    pageIdLabel = brand === 'atc' ? `-${pageId}` : ' -classlistnew';
  }
  const cdl = {
    eventProperties: {
      vehicle: PixallInventoryTransformer.getVehicleCommonDataLayer(inventory)
    }
  };

  // Handle Common DataLayer for atc and user's logged in or NOT and click to save a vehicle in carousel listing
  if (!wasSaved && (brand === 'atc' || isLoggedIn) && isSimilarListingInVDPPage) {
    cdl.eventProperties.event = {
      valueEventCode: 'F757'
    };
  }
  const wasSavedListingEventCdl = {
    eventProperties: {
      vehicle: getCDLVehicleTransformer(vehicleData),
      ...(recommendationsCategory && {
        event: {
          valueEventCode: 'F757'
        }
      })
    }
  };
  // Handle cdl for user's logged in and click save a premium spotlight vehicle in atc/kbb srp
  if (isLoggedIn && !(isBuildAndPricePage || isSimilarListingInVDPPage) && isPremiumSpotlight(par)) {
    if (wasSaved) {
      addListingTriggerData(par, wasSavedListingEventCdl);
    } else {
      addListingTriggerData(par, cdl);
    }
    pixallData.listingType = 'highlight';
  }
  const pixallPayload = {
    pixallData: {
      ...pixallData,
      clickTypeToListingType: !pixallData.listingType && isVDP(dataLayer) // only needed if pixallData.listingType is not passed to this function
    },
    eventSource: 'vehicle',
    eventType: pixallData.eventType || 'save',
    ...(!wasSaved && {
      feature: 'Drive the 5'
    }),
    par,
    stockNumber: inventory.stockId,
    inventory: {
      ...inventory,
      clickType
    },
    exteriorColor,
    listingType: pixallData.listingType,
    chromeStyleId: pageId === 'fyd_ddp' ? chromeStyleId : undefined,
    cdl
  };
  if (wasSaved) {
    const eventData = {
      vehicle: {
        ...vehicleData,
        clickType: listingCategory
      }
    };
    Google.sendClickEvent({
      eventElement: detailPageName.includes('my') ? getBrandUnit(brand) : 'save-car',
      // populates 'my-at' or 'my-kbb' for my-at/my-kbb, 'save-car' for others
      eventAction: pageNameList.includes(detailPageName) ? 'save' : 'click',
      eventResult: `remove::saved-car::${detailPageName.includes('my') ? 'own' : 'vin' + categoryString + stickyString}`,
      // populates 'own' for my-at/my-kbb, 'vin' for others. Category string will not apply to my-at/my-kbb pages
      eventData,
      ...(eventName && {
        eventName
      })
    }, option);
    delete pixallPayload.feature;
    const removePrefix = isHybridVDP ? 'remove::saved-car::' : 'remove_saved-car_';
    const removeSuffix = isHybridVDP ? pixallData.listingType : listingCategory;
    const removeSegment = isHybridVDP ? '::' : '_';
    if (loggedInVdpAndSrp) {
      Pixall.sendClickEvent({
        ...pixallPayload,
        ...(recommendationsCategory && {
          feature: 'Drive the 5'
        }),
        label: `${removePrefix}${detailPageName.includes('my') ? 'own' : 'vin'}${removeSegment}${removeSuffix}`,
        eventType: 'click',
        cdl: wasSavedListingEventCdl
      }, dataLayer, option);
    }
  } else if
    // quick save feature is disabled, uncomment the condition below in case it's brought back
    // (isLoggedIn || (!isLoggedIn && brand !== 'kbb' && savedListing.length < maxQuickSavesLimit))
  (isLoggedIn || !isLoggedIn && brand !== 'kbb') {
    let eventData = {}; // DSRP, etc.
    if (shouldIncludeClickType) {
      eventData = {
        vehicle: {
          ...vehicleData,
          clickType: listingCategory
        }
      };
    } else if (detailPageName === 'ddp') {
      eventData = {
        vehicle: {
          ...vehicleData,
          dealershipId: inventory?.owner?.id?.toString()
        }
      };
    } else if (detailPageName === 'vdp' || detailPageName === 'srp_dealer' || detailPageName === 'hybrid_vdp') {
      eventData = {
        vehicle: {
          ...vehicleData,
          clickType: listingCategory,
          dealershipId: inventory?.owner?.id?.toString()
        }
      };
    }
    if (pixallData.listingType) {
      pixallPayload.listingType = pixallData.listingType;
    }
    pixallPayload.label = `${brand === 'kbb' ? 'myKBB' : 'myATC'} - ${cmp}${pageIdLabel}`;
    pixallPayload.cdl.eventProperties.event = {
      valueEventCode: 'F757'
    };
    if (loggedInVdpAndSrp) {
      Pixall.sendClickEvent({
        ...pixallPayload,
        feature: 'Drive the 5',
        ...(detailPageName === 'ddp' && {
          searchFacetVehicleTypes: pixallData.listingType
        })
      }, dataLayer, option);
    }
    const loginPrefix = isHybridVDP ? 'login' : 'logged-in';
    Google.sendClickEvent({
      eventElement: 'save-car',
      eventAction: 'save',
      eventResult: `save_${isLoggedIn ? loginPrefix : 'quick'}::vehicle${categoryString}${stickyString}${nbaString}${invString}`,
      eventData,
      ...(eventName && {
        eventName
      })
    }, option);
  }

  // return data for custom event
  return {
    cmp,
    par
  };
}

/**
 * @summary Sends Google Click event on carousel pagination (left/right arrows)
 * @cmp next, previous
 * @data ownerId, direction
 * @events event1
 */
export function inventoryCarouselPagination(_ref4, option) {
  let {
    data = {}
  } = _ref4;
  const {
    direction,
    isRecentSearches
  } = data;
  if (isRecentSearches) {
    Google.sendClickEvent({
      eventElement: 'search-car',
      eventResult: 'cta::srp::continue-your-search::scroll::carousel',
      eventNonInteractive: false
    }, option);
  }

  // return data for custom event
  return {
    cmp: direction
  };
}

/**
 * @summary Sends Google Click event when ellipsses is clicked on a simplified listing
 * @cmp Adobe-ellipses_btn GA-cta::ellipses
 * @events event1
 */
export function inventoryListingViewEllipsesClick(metadata, option) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    inventoryId,
    par = ''
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const listingCategory = getListingCategory(par);
  const listingType = isPremiumSpotlight(par) ? 'highlight' : undefined;
  Pixall.sendClickEvent({
    eventSource: 'vehicle',
    eventType: 'click',
    inventory,
    label: `ellipsis_btn-${listingCategory}`,
    par,
    listingType,
    cdl: addListingTriggerData(par, {})
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventAction: 'click',
    eventResult: `cta::ellipses::${listingCategory}`
  }, option);
  return {};
}
/**
 * @summary Sends Pixall/Google Click event when product tile is clicked on a listing
 * @cmp ec_pa_lgo ( tile/image) / ec_pa_txt (href)
 * @data inventoryId, tileIndex
 * @events event1
 */
export function inventoryProductClick(_ref5, option) {
  let {
    data = {},
    dataLayer,
    state,
    ...rest
  } = _ref5;
  const {
    inventoryId,
    epn,
    pageType
  } = data;
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const inventory = getInventoryOwnerById(state, inventoryId);
  let pt = pageType;
  if (pageId === 'fyc_vdp') {
    pt = 'vdp';
  } else if (pageId === 'fyc_srl') {
    pt = 'vlp';
  } else if (pageId === 'fyd_srl' || pageId === 'fyd_ddp') {
    pt = 'dealer';
  }

  // return data for custom event
  return productTileClickHelper({
    data: {
      ...data,
      inventory,
      epn,
      pageType: pt
    },
    dataLayer,
    pixallData: {
      inventory,
      cdl: {
        eventProperties: {
          event: {
            valueEventCode: valueEventCode.WESTLAKE
          }
        }
      }
    },
    state,
    ...rest
  }, option);
}

/**
 * @summary Sends Google impressions for listings
 * @epn carfax (vehicle history report)
 * @data epn, onwerId
 */
export function inventoryProductImpressions(metadata, option) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    inventoryId,
    epn,
    clickType,
    paginatedListingIndex
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isSiriusXM = epn === 'sirius_xm';
  const eventSource = isSiriusXM ? 'vendor' : 'vehicle-history-report';
  let vehicleData = {
    clickType,
    listingPosition: paginatedListingIndex
  };
  if (inventoryId) {
    const pageName = _get(dataLayer, 'page.pageName', '');
    const inheritPageData = !['vdp'].includes(pageName);
    vehicleData = googleInventoryTransformer(inventory, {
      target: 'vehicle',
      dataLayer
    }, inheritPageData);
  }
  const productTiles = _get(inventory, 'productTiles', []).filter(tile => tile && tile.tileType !== 'CERTIFIED_CAR');
  const epnValue = epn || _get(inventory, 'productTiles[0].epn', '') || transformTileType({
    tileType: _get(productTiles, '[0]tileType', ''),
    isPrivateSeller: _get(inventory, 'owner.privateSeller', false)
  });

  // return data for custom event
  Google.sendImpressions(metadata, {
    eventElement: 'partner',
    eventResult: `${eventSource}::${isSiriusXM ? 'siriusxm' : epnValue}`,
    eventData: !isSiriusXM ? {
      vehicle: vehicleData
    } : {}
  }, option);
  return {
    eventSource,
    epn
  };
}

/**
 * @summary Sends Google/Pixall Click event when certified tile is clicked on a listing
 * @data inventoryId
 * @events event1
 */
export function inventoryCertifiedTileClick(_ref6, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref6;
  const {
    inventoryId,
    par = 'v_lstg',
    eventSource: customEventSource,
    clickType
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const make = _get(inventory, 'make.name', inventory.make) || '';
  const epn = 'CERT';
  let cmp = 'ec_pa_lgo';
  let label = `${cmp}-${epn}-${pageId}`;

  // GA360 data
  let eventElement = 'partner';
  let eventResult = `certified::${make}`;
  let eventData = {
    vehicle: {
      clickType
    }
  };
  if (data.co_txt_url && data.co_txt_url.includes('Inspection')) {
    cmp = 'inspt_rpt';
    label = `${cmp}-${pageId}`;
    const linkCopy = event?.target?.text || event?.target?.title || '';
    eventElement = state.currentPageName;
    eventResult = `text-link::${_kebabCase(linkCopy)}`;
    eventData = {};
  }
  Pixall.sendClickEvent({
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    eventSource: customEventSource || 'oem',
    eventType: 'click',
    inventory,
    label,
    par
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement,
    eventResult,
    eventData
  }, option);
  return {
    cmp
  };
}

/**
 * @summary Sends Adobe events for keyword search button
 * @cmp spec_v
 * @data filterDuck
 * @events event1
 */
export async function inventorySearch(_ref7, option) {
  let {
    data = {},
    dataLayer
  } = _ref7;
  const {
    cmp: customCmp,
    keywordPhrases: name,
    rawText = '',
    tagIndex
  } = data;
  const cmp = customCmp || 'spec_v';

  // there was a case it is an array
  const keywordName = name.toString().replace(/\s+/g, '-').toLowerCase();
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'search',
    eventResult: `keyword::${keywordName}`
  }, option);
  const pixallKeywordName = _kebabCase(rawText);
  Pixall.sendClickEvent({
    label: `keyword::${pixallKeywordName}`,
    eventSource: 'button',
    eventType: 'click',
    cdl: {
      eventProperties: {
        vehicleSearch: {
          searchKeywords: pixallKeywordName
        },
        tagIndex: isVDP(dataLayer) ? tagIndex : undefined
      }
    }
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends Pixall event for View All Features Button click
 * @events event167
 */
export async function inventoryViewAllFeaturesClick(_ref8, option) {
  let {
    dataLayer
  } = _ref8;
  Pixall.sendClickEvent({
    label: 'cta::view-all-features',
    eventSource: 'vehicle',
    cdl: {
      eventProperties: {
        tagIndex: 'vdp-042',
        element: 'View All Features Button',
        action: 'Click'
      }
    }
  }, dataLayer, option);
  return {};
}

/**
 * @summary  GA360 impressions tag for Inventory Scroll
 */
export async function inventoryScrollImpressions(metadata, option) {
  const {
    data = {}
  } = metadata;
  const {
    scroll
  } = data;
  Google.sendImpressions(metadata, {
    eventElement: 'scroll',
    eventResult: `scroll::${scroll}%-milestone`,
    eventNonInteractive: false,
    eventData: {}
  }, option);
  return {};
}

/**
 * @summary Sends Pixall impressions for Recommendation filmstrip
 */
export function inventorySimilarListingsImpression(_ref9, option) {
  let {
    data = {},
    dataLayer = {},
    state,
    history
  } = _ref9;
  const {
    selectedItemIds = []
  } = data;
  let resultsWithOwner = [];
  if (selectedItemIds?.length > 0) {
    const results = selectedItemIds.map(id => ({
      id
    }));
    resultsWithOwner = results.map(function (inventory) {
      if (inventory === void 0) {
        inventory = {};
      }
      return getInventoryOwnerById(state, inventory.id);
    });
    const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
    PixallInventoryTransformer.buildListingChunks(resultsWithOwner, {
      rank: 1,
      listingType: 'recommendations'
    }).forEach(listingChunk => {
      Pixall.sendImpression({
        dataLayer,
        history
      }, {
        eventSource: 'vehicle',
        label: pageId,
        listingType: 'recommendations',
        vehicles: listingChunk
      }, option);
    });
  } else {
    throw new Error('selectedItemIds is missing');
  }
  return {
    results: resultsWithOwner
  };
}
export default {
  inventoryCarouselPagination,
  inventoryCertifiedTileClick,
  inventoryClick,
  inventoryImpressions,
  inventoryListingViewEllipsesClick,
  inventoryProductClick,
  inventoryProductImpressions,
  inventorySave,
  inventorySearch,
  inventorySimilarListingsImpression,
  inventoryScrollImpressions,
  inventoryViewAllFeaturesClick
};